import {showCommunicationError, showNotFoundToast, showSuccess} from 'helpers/alerts';
import {t} from 'i18next';
import update from 'immutability-helper';
import {action, makeObservable, observable, runInAction} from 'mobx';
import {redirectTo} from 'routing/browserHistory';
import {AppRoutePaths} from 'routing/Routes';
import {appState} from 'store/AppStore';
import {AttributeDefinitions} from 'views/vehicles/utils/GarageSpotAttributeSelectionModal';
import {
  CarEditDto,
  CarEquipmentDto,
  CarMainDto,
  CarServiceDto,
  CarTechDto,
  GarageSpotAttribute,
  GarageSpotAttributeDefinitionDto,
  GarageSpotAttributeDto,
  Int32GenericApiResponse,
  ReservationDto,
  ServiceActionDto,
  SortDirection,
  SuccessfullApiResponse,
} from '../../../../../PRMGApiClient';
import {Api} from '../../../../../PRMGApiClient/ApiClient';
import {VehicleEditKey} from '../../views/vehicles/VehicleEditView';

export class VehicleState {
  @observable
  item: CarEditDto = {
    carId: undefined,
    main: undefined,
    photos: undefined,
    tech: undefined,
    service: undefined,
    tires: undefined,
    notes: undefined,
    attachments: undefined,
    garageSpot: undefined,
  };

  @observable
  dirty: Record<VehicleEditKey, boolean> = {
    additionalPhotos: false,
    carEquipment: false,
    files: false,
    overview: false,
    parkingSpotAttributes: false,
    serviceData: false,
    keySpecifications: false,
    photos: false,
  };

  @observable
  serviceActions?: ServiceActionDto[] = undefined;

  @observable
  reservations?: ReservationDto[] = undefined;

  @observable
  isLoading: boolean = false;

  constructor() {
    makeObservable(this);
  }

  @action
  updateImages(dragIndex: number, hoverIndex: number): void {
    if (this.item.photos && this.item.photos.photoFiles) {
      this.item.photos.photoFiles = update(this.item.photos.photoFiles, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, this.item.photos.photoFiles[dragIndex]],
        ],
      });
    }
  }

  updateImagesPost() {
    if (this.item.carId && this.item.photos) {
      const orderedPhotoFiles = this.item.photos.photoFiles?.map((elem, indx) => ({...elem, order: indx + 1}));
      Api.Car.apiCarChangephotoorderPost({
        changePhotoOrderRequest: {
          carId: this.item.carId,
          orderedFiles: orderedPhotoFiles,
        },
      })
        .then(() => {
          appState.getCommonData();
        })
        .catch(showCommunicationError);
    }
  }

  loadVehicle(vehicleId: number | undefined) {
    if (!vehicleId) return;

    this.isLoading = true;
    Api.Car.getEditDtoByIdAsync({
      id: vehicleId,
    })
      .then((res) => {
        if (res.content) {
          runInAction(() => {
            this.item = res.content ?? this.item;
          });
          Api.ServiceAction.apiServiceActionFindByGet({
            filtersCarId: vehicleId,
            sortPropertyName: 'startDateTime',
            sortDirection: SortDirection.Desc,
            pageSize: 4,
            pageNumber: 1,
          })
            .then((res) => {
              if (res.content) {
                runInAction(() => {
                  this.serviceActions = res.content?.items ?? [];
                });
              }
            })
            .catch(showCommunicationError);
          Api.Reservation.apiReservationFindByGet({
            filtersCarId: vehicleId,
            sortPropertyName: 'startDateTime',
            sortDirection: SortDirection.Asc,
            pageSize: 2,
            pageNumber: 1,
          })
            .then((res) => {
              if (res.content) {
                runInAction(() => {
                  this.reservations = res.content?.items ?? [];
                });
              }
            })
            .catch(showCommunicationError);
        } else {
          showNotFoundToast();
          setTimeout(() => redirectTo(AppRoutePaths.garages.url), 1000);
        }
      })
      .catch(showCommunicationError)
      .finally(() => (this.isLoading = false));
  }

  save(payload: CarMainDto, toExit: boolean = false): void {
    this.item.main = payload;
    this.item.carId ? this.updateVehicle() : this.addVehicle(toExit);
  }

  addVehicle(toExit: boolean): void {
    this.isLoading = true;
    Api.Car.apiCarPost({
      carMainDto: this.item.main,
    })
      .then((res) => {
        const carId = res.content?.carId;
        appState.getCommonData();
        showSuccess(t('Car created'));
        redirectTo(
          `${AppRoutePaths.garages.url}/${this.item.main?.garageId}/vehicles/${carId}` + (toExit ? '' : '/edit')
        );
      })
      .catch(showCommunicationError)
      .finally(() => (this.isLoading = false));
  }

  removeVehicle(): void {
    this.isLoading = true;
    if (this.item.carId) {
      Api.Car.apiCarCarIdDelete({
        carId: this.item.carId,
      })
        .then(() => {
          appState.getCommonData();
          showSuccess(t('Car deleted'));
          redirectTo(`${AppRoutePaths.garages.url}/${this.item.main?.garageId}`);
        })
        .catch(showCommunicationError)
        .finally(() => (this.isLoading = false));
    }
  }

  updateVehicle(): void {
    this.isLoading = true;
    if (this.item.carId) {
      Api.Car.apiCarMainCarIdPut({
        carId: this.item.carId,
        carMainDto: {
          ...this.item.main,
          currentWorth: this.item.main?.currentWorth?.toString() === '' ? null : this.item.main?.currentWorth,
        },
      })
        .then(() => {
          appState.getCommonData();
          this.onSuccess();
        })
        .catch(showCommunicationError)
        .finally(() => (this.isLoading = false));
    }
  }

  updateTech(payload: CarTechDto): void {
    this.isLoading = true;
    if (this.item.carId) {
      Api.Car.apiCarTechCarIdPut({
        carId: this.item.carId,
        carTechDto: payload,
      })
        .then(() => this.onSuccess())
        .catch(showCommunicationError)
        .finally(() => (this.isLoading = false));
    }
  }

  addCarEquipment(payload: CarEquipmentDto): Promise<void> {
    return Api.GarageSpotApi.apiGarageSpotAddEquipmentPost({
      carEquipmentDto: payload,
    })
      .then(() => {
        this.onSuccess();
        this.loadVehicle(this.item.carId);
      })
      .catch(showCommunicationError);
  }

  removeCarEquipment(id: number): Promise<void> {
    return Api.GarageSpotApi.apiGarageSpotDeleteEquipmentDelete({
      id,
    })
      .then(() => {
        this.onSuccess();
        this.loadVehicle(this.item.carId);
      })
      .catch(showCommunicationError);
  }

  updateCarEquipment(payload: CarEquipmentDto): Promise<void> {
    return Api.GarageSpotApi.apiGarageSpotUpdateEquipmentPut({
      carEquipmentDto: payload,
    })
      .then(() => {
        this.onSuccess();
        this.loadVehicle(this.item.carId);
      })
      .catch(showCommunicationError);
  }

  updateService(payload: CarServiceDto): void {
    this.isLoading = true;
    if (this.item.carId) {
      Api.Car.apiCarServiceCarIdPut({
        carId: this.item.carId,
        carServiceDto: payload,
      })
        .then(() => this.onSuccess())
        .catch(showCommunicationError)
        .finally(() => (this.isLoading = false));
    }
  }

  updatePhotoFiles(payload: File[]): void {
    this.isLoading = true;
    if (this.item.carId) {
      Api.Car.apiCarPhotosCarIdPost({
        carId: this.item.carId,
        request: payload,
      })
        .then((response) => {
          if (!this.item.photos?.mainPhoto && this.item.carId) {
            Api.Car.apiCarReplacecarmainphotobyidCarIdFileIdPost({
              carId: this.item.carId,
              fileId: response.content?.[0].fileId ?? 0,
            });
          }
        })
        .then(() => {
          appState.getCommonData();
          this.onSuccess();
        })
        .catch(showCommunicationError)
        .finally(() => (this.isLoading = false));
    }
  }

  removePhotoFile(fileId?: number): void {
    if (fileId === undefined) return;
    this.isLoading = true;
    if (this.item.carId) {
      Api.Car.apiCarPhotosDelete({
        carId: this.item.carId,
        fileId,
      })
        .then(() => this.onSuccess())
        .catch(showCommunicationError)
        .finally(() => (this.isLoading = false));
    }
  }

  updateAttachments(payload: File[]): void {
    this.isLoading = true;
    if (this.item.carId) {
      Api.Car.apiCarAttachmentsCarIdPost({
        carId: this.item.carId,
        request: payload,
      })
        .then(() => this.onSuccess())
        .catch(showCommunicationError)
        .finally(() => (this.isLoading = false));
    }
  }

  removeAttachment(fileId?: number): void {
    if (fileId === undefined) return;
    this.isLoading = true;
    if (this.item.carId) {
      Api.Car.apiCarAttachmentsCarIdDelete({
        carId: this.item.carId,
        fileId,
      })
        .then(() => this.onSuccess())
        .catch(showCommunicationError)
        .finally(() => (this.isLoading = false));
    }
  }

  updateModelFiles(payload: File[]): void {
    this.isLoading = true;
    if (this.item.carId) {
      Api.Car.apiCarAddModelFileCarIdPost({
        carId: this.item.carId,
        request: payload,
      })
        .then(() => this.onSuccess())
        .catch(showCommunicationError)
        .finally(() => (this.isLoading = false));
    }
  }

  removeModelFile(fileId?: number): void {
    if (fileId === undefined) return;
    this.isLoading = true;
    if (this.item.carId) {
      Api.Car.apiCarDeletemodelfileCarIdPost({
        carId: this.item.carId,
        fileId,
      })
        .then(() => this.onSuccess())
        .catch(showCommunicationError)
        .finally(() => (this.isLoading = false));
    }
  }

  updateGarageSpotAttributes({
    toAdd,
    toRemove,
  }: {
    toAdd: AttributeDefinitions[];
    toRemove: GarageSpotAttribute[];
  }): Promise<(Int32GenericApiResponse | SuccessfullApiResponse)[]> {
    const promises = [];

    // Add new attributes to the garage spot with empty values
    for (const attribute of toAdd) {
      promises.push(
        Api.GarageSpotAttributes.apiGarageSpotAttributesAddPost({
          garageSpotAttributeDto: {
            garageSpotId: this.item.garageSpot?.id,
            value: '',
            attributeDefinitionId: attribute.id,
          },
        })
      );
    }

    // Remove attributes from the garage spot
    for (const attribute of toRemove) {
      if (attribute.id === undefined) continue;
      promises.push(
        Api.GarageSpotAttributes.apiGarageSpotAttributesDeleteIdDelete({
          id: attribute.id,
        })
      );
    }
    this.isLoading = true;
    return Promise.all(promises);
  }

  // Save attributes with values
  saveAttributes(attributes: GarageSpotAttributeDto[]): void {
    const promises = [];
    for (const attribute of attributes) {
      promises.push(
        Api.GarageSpotAttributes.apiGarageSpotAttributesUpdatePut({
          garageSpotAttributeUpdateDto: attribute,
        })
      );
    }
    this.isLoading = true;
    Promise.all(promises).then(() => this.onSuccess());
  }

  addAttributeDefinition(payload: GarageSpotAttributeDefinitionDto): void {
    this.isLoading = true;
    Api.GarageSpotAttributeDefinition.apiGarageSpotAttributeDefinitionAddPost({
      garageSpotAttributeDefinitionDto: payload,
    })
      .then(() => {
        showSuccess(t('Changes saved'));
        appState.getCommonData();
      })
      .catch(showCommunicationError)
      .finally(() => (this.isLoading = false));
  }

  onSuccess(): void {
    showSuccess(t('Changes saved'));
    this.loadVehicle(this.item.carId);
  }

  @action
  setDirty(key: VehicleEditKey, isDirty: boolean) {
    this.dirty[key] = isDirty;
  }
}
